import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { format } from "date-fns";
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  Building2,
  Image,
  CheckCircle,
  XCircle,
  Clock as ClockIcon,
  Brain,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import PhotoGrid from "@/components/photos/PhotoGrid";
import PhotoDetailModal from "@/components/photos/PhotoDetailModal";
import { toast } from "sonner";

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  // Fetch job details
  const { data: job, isLoading: loadingJob } = useQuery({
    queryKey: ["job", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("jobs")
        .select("*")
        .eq("id", id)
        .single();
      if (error) throw error;
      return data;
    },
  });

  // Fetch photos for this job
  const { data: photos = [], isLoading: loadingPhotos } = useQuery({
    queryKey: ["job-photos", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("photos")
        .select("*")
        .eq("job_id", id)
        .order("taken_at", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  // Fetch all jobs for assignment dropdown
  const { data: allJobs = [] } = useQuery({
    queryKey: ["jobs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("jobs")
        .select("*")
        .order("scheduled_start", { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const assignMutation = useMutation({
    mutationFn: async ({ photoId, jobId }) => {
      const targetJob = allJobs.find((j) => j.id === jobId);
      const { error } = await supabase
        .from("photos")
        .update({
          job_id: jobId,
          property_id: targetJob?.property_id,
          assignment_status: "manual_assigned",
        })
        .eq("id", photoId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["job-photos", id] });
      queryClient.invalidateQueries({ queryKey: ["photos"] });
      setSelectedPhoto(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (photoId) => {
      const { error } = await supabase
        .from("photos")
        .delete()
        .eq("id", photoId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["job-photos", id] });
      queryClient.invalidateQueries({ queryKey: ["photos"] });
      setSelectedPhoto(null);
    },
  });

  const analyzeMutation = useMutation({
    mutationFn: async ({ photoIds, userContext }) => {
      const idArray = Array.isArray(photoIds) ? photoIds : [photoIds];
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/analyze-photo`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({
            photo_ids: idArray,
            user_context: userContext || null,
          }),
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Analysis failed");
      }
      
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["job-photos", id] });
      queryClient.invalidateQueries({ queryKey: ["photos"] });
      queryClient.invalidateQueries({ queryKey: ["photo-defects"] });
      
      const successCount = data.results?.filter(r => r.success).length || 0;
      const totalDefects = data.results?.reduce((sum, r) => sum + (r.defects_found || 0), 0) || 0;
      
      toast.success(`Analyzed ${successCount} photo(s)`, {
        description: `Found ${totalDefects} defect(s)`,
      });
    },
    onError: (error) => {
      toast.error("Analysis failed", {
        description: error.message,
      });
    },
  });

  const isLoading = loadingJob || loadingPhotos;

  // Calculate analysis statistics
  const totalPhotos = photos.length;
  const analyzedPhotos = photos.filter(p => p.analysis_status === "completed").length;
  const analyzingPhotos = photos.filter(p => p.analysis_status === "analyzing").length;
  const failedPhotos = photos.filter(p => p.analysis_status === "failed").length;
  const notAnalyzedPhotos = totalPhotos - analyzedPhotos - analyzingPhotos - failedPhotos;
  const analysisPercentage = totalPhotos > 0 ? Math.round((analyzedPhotos / totalPhotos) * 100) : 0;

  const statusColors = {
    scheduled: "bg-blue-100 text-blue-700",
    in_progress: "bg-amber-100 text-amber-700",
    completed: "bg-emerald-100 text-emerald-700",
    cancelled: "bg-slate-100 text-slate-600",
  };

  const typeLabels = {
    initial: "Initial",
    progress: "Progress",
    final: "Final",
    warranty: "Warranty",
    special: "Special",
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <div className="w-8 h-8 bg-slate-200 rounded animate-pulse" />
          <div className="h-8 bg-slate-200 rounded w-48 animate-pulse" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {Array(4).fill(0).map((_, i) => (
            <div key={i} className="h-32 bg-slate-200 rounded-xl animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="text-center py-16">
        <p className="text-slate-500">Job not found</p>
        <Button onClick={() => navigate("/Jobs")} className="mt-4">
          Back to Jobs
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/Jobs")}
          className="flex-shrink-0"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold text-slate-900">
              {job.property_name || "Unknown Property"}
            </h1>
            <Badge className={`${statusColors[job.status]} border-0`}>
              {job.status?.replace("_", " ")}
            </Badge>
            <Badge variant="outline">
              {typeLabels[job.job_type] || job.job_type}
            </Badge>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 text-sm text-slate-500">
            <div className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {job.inspector_name || "Unassigned"}
            </div>
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {job.scheduled_start
                ? format(new Date(job.scheduled_start), "MMM d, yyyy HH:mm")
                : "No date"}
              {job.scheduled_end && ` - ${format(new Date(job.scheduled_end), "HH:mm")}`}
            </div>
          </div>
        </div>
      </div>

      {/* Analysis Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-100">
              <Image className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{totalPhotos}</p>
              <p className="text-xs text-slate-500">Total Photos</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-100">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{analyzedPhotos}</p>
              <p className="text-xs text-slate-500">Analyzed</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-violet-100">
              <Brain className="w-5 h-5 text-violet-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{analyzingPhotos}</p>
              <p className="text-xs text-slate-500">Analyzing</p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-slate-100">
              <ClockIcon className="w-5 h-5 text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{notAnalyzedPhotos}</p>
              <p className="text-xs text-slate-500">Not Analyzed</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Analysis Progress */}
      <Card className="p-6">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-slate-900">Analysis Progress</h3>
            <span className="text-2xl font-bold text-slate-900">{analysisPercentage}%</span>
          </div>
          <Progress value={analysisPercentage} className="h-3" />
          <div className="flex items-center justify-between text-sm text-slate-500">
            <span>{analyzedPhotos} of {totalPhotos} photos analyzed</span>
            {failedPhotos > 0 && (
              <span className="flex items-center gap-1 text-rose-600">
                <XCircle className="w-4 h-4" />
                {failedPhotos} failed
              </span>
            )}
          </div>
        </div>
      </Card>

      {/* Photos Grid */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-slate-900">Photos</h2>
          {notAnalyzedPhotos > 0 && (
            <Button
              onClick={() =>
                analyzeMutation.mutate({
                  photoIds: photos.filter(p => !p.analysis_status || p.analysis_status === "pending").map(p => p.id),
                  userContext: null,
                })
              }
              disabled={analyzeMutation.isPending}
              className="gap-2 bg-violet-600 hover:bg-violet-700"
            >
              {analyzeMutation.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Brain className="w-4 h-4" />
              )}
              Analyze All Unanalyzed
            </Button>
          )}
        </div>

        {photos.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
            <Image className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500">No photos assigned to this job yet</p>
          </div>
        ) : (
          <PhotoGrid 
            photos={photos} 
            onPhotoClick={setSelectedPhoto}
            selectMode={false}
            selectedIds={new Set()}
          />
        )}
      </div>

      {/* Photo Detail Modal */}
      <PhotoDetailModal
        photo={selectedPhoto}
        jobs={allJobs}
        onClose={() => setSelectedPhoto(null)}
        onAssign={(photoId, jobId) => assignMutation.mutate({ photoId, jobId })}
        isAssigning={assignMutation.isPending}
        onDelete={(photoId) => deleteMutation.mutate(photoId)}
        isDeleting={deleteMutation.isPending}
        onAnalyze={(photoId, userContext) =>
          analyzeMutation.mutate({ photoIds: [photoId], userContext })
        }
        isAnalyzing={analyzeMutation.isPending}
      />
    </div>
  );
}
